import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Save, Loader2, Building2, BadgeCheck, ShieldAlert } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import VendorDocuments from "@/components/VendorDocuments";
import { ChangePassword } from "@/components/ChangePassword";
import { DeviceSessions } from "@/components/DeviceSessions";

const CONTRACTOR_DOC_TYPES = ["Trade Licence", "VAT / TRN Certificate"];

export default function ContractorProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/public/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        company_name: user.company_name || "",
        address: user.address || "",
        phone: user.phone || "",
        contact_email: user.contact_email || "",
        city: user.city || "",
        country: user.country || "",
        trade_license_no: user.trade_license_no || "",
        vat_number: user.vat_number || "",
      });
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  if (!form) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const verified = !!user?.verified;

  return (
    <div className="max-w-4xl" data-testid="contractor-profile-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Settings</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">Company Profile</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your company details, country and verification documents.
          </p>
        </div>
        <Button data-testid="save-profile-button" onClick={save} disabled={saving} className="gap-2 h-11 font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Profile
        </Button>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        {/* Verification status */}
        <div
          data-testid="verify-status-card"
          className={`rounded-xl border p-5 flex items-center gap-4 ${
            verified
              ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900/50"
              : "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/50"
          }`}
        >
          {verified ? (
            <BadgeCheck className="w-8 h-8 text-emerald-600 dark:text-emerald-400 shrink-0" />
          ) : (
            <ShieldAlert className="w-8 h-8 text-amber-600 dark:text-amber-400 shrink-0" />
          )}
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100">
              {verified ? "Verified contractor" : "Not verified yet"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {verified
                ? "Vendors see a Verified badge on your RFQs — it builds trust and gets you faster quotes."
                : "Upload an in-date Trade Licence and VAT / TRN Certificate below to earn your Verified badge."}
            </div>
          </div>
        </div>

        {/* Identity */}
        <div className="bg-card border border-border rounded-xl shadow-sm">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
            <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">Company Details</div>
              <div className="text-xs text-muted-foreground">Shown to vendors on your RFQs</div>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <Label>Company Name</Label>
              <Input data-testid="profile-company-name" value={form.company_name} onChange={set("company_name")} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>City</Label>
                <Input data-testid="profile-city" value={form.city} onChange={set("city")} className="mt-1.5" />
              </div>
              <div>
                <Label>Country</Label>
                <select data-testid="profile-country" value={form.country || ""}
                        onChange={set("country")}
                        className="mt-1.5 flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <option value="">Select country</option>
                  {form.country && !countries.includes(form.country) && <option value={form.country}>{form.country}</option>}
                  {countries.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                </select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="profile-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" />
              </div>
              <div>
                <Label>Contact Email (used on RFQs)</Label>
                <Input data-testid="profile-contact-email" value={form.contact_email} onChange={set("contact_email")} className="mt-1.5" />
              </div>
              <div>
                <Label>Trade Licence No.</Label>
                <Input data-testid="profile-licence" value={form.trade_license_no} onChange={set("trade_license_no")} className="mt-1.5" />
              </div>
              <div>
                <Label>VAT / TRN Number</Label>
                <Input data-testid="profile-vat" value={form.vat_number} onChange={set("vat_number")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea data-testid="profile-address" value={form.address} onChange={set("address")} rows={2} className="mt-1.5" placeholder="Street, City, Country, P.O. Box" />
            </div>
          </div>
        </div>

        <VendorDocuments
          docTypes={CONTRACTOR_DOC_TYPES}
          onChange={refreshUser}
          heading="Verification Documents"
          subheading="Upload your Trade Licence and VAT / TRN Certificate. AI reads the expiry and you earn a Verified badge."
          emptyHint="No documents yet. Upload your Trade Licence and VAT / TRN Certificate to earn the Verified badge shown to vendors."
        />

        <ChangePassword />

        <DeviceSessions />

        <div className="flex justify-end pb-4">
          <Button data-testid="save-profile-button-bottom" onClick={save} disabled={saving} className="gap-2 h-11 font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Profile
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
